import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, ArrowLeft, ArrowRight, Upload, Loader2, X, Link as LinkIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface EditDocumentsProps {
  kbData: any;
  onUpdate: (updates: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const EditDocuments = ({ kbData, onUpdate, onNext, onBack }: EditDocumentsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploadedFiles, setUploadedFiles] = useState<any[]>(kbData.uploadedFiles || []);
  const [links, setLinks] = useState<string[]>(kbData.links || []);
  const [linkInput, setLinkInput] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setUploadedFiles(kbData.uploadedFiles || []);
    setLinks(kbData.links || []);
  }, [kbData]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const newFiles: any[] = [];

    try {
      for (const file of Array.from(files)) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${user?.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        
        const { error: uploadError, data } = await supabase.storage
          .from('knowledge-bases')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('knowledge-bases')
          .getPublicUrl(fileName);

        newFiles.push({
          name: file.name,
          url: publicUrl,
          size: file.size,
          type: file.type,
        });
      }

      setUploadedFiles([...uploadedFiles, ...newFiles]);
      toast({
        title: "Files Uploaded",
        description: `${newFiles.length} file(s) uploaded successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload files.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const handleAddLink = () => {
    if (linkInput.trim() && !links.includes(linkInput.trim())) {
      setLinks([...links, linkInput.trim()]);
      setLinkInput("");
    }
  };

  const handleRemoveLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    onUpdate({
      uploadedFiles,
      links,
    });
    onNext();
  };

  return (
    <Card className="border-2 border-black">
      <CardHeader className="bg-slate-100 border-b border-black">
        <CardTitle className="font-sans flex items-center">
          <FileText className="mr-2" size={24} />
          Content Sources
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <Label className="text-base font-semibold">Upload Documents</Label>
            <p className="text-sm text-gray-500 mb-3">
              Upload PDFs, documents, or other files related to this knowledge base
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                {isUploading ? (
                  <Loader2 className="h-12 w-12 text-gray-400 mb-2 animate-spin" />
                ) : (
                  <Upload className="h-12 w-12 text-gray-400 mb-2" />
                )}
                <span className="text-sm text-gray-600">
                  {isUploading ? "Uploading..." : "Click to upload or drag and drop"}
                </span>
              </label>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <FileText size={16} />
                      <span className="text-sm">{file.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-base font-semibold">External Links</Label>
            <p className="text-sm text-gray-500 mb-3">
              Add URLs to external resources, references, or related content
            </p>
            <div className="flex gap-2">
              <Input
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLink())}
                placeholder="https://example.com/resource"
                type="url"
              />
              <Button
                type="button"
                onClick={handleAddLink}
                variant="outline"
                className="border-2 border-black"
              >
                <LinkIcon size={16} className="mr-1" />
                Add
              </Button>
            </div>

            {links.length > 0 && (
              <div className="mt-4 space-y-2">
                {links.map((link, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <LinkIcon size={16} />
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate"
                      >
                        {link}
                      </a>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLink(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="border-2 border-black"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              className="bg-black hover:bg-black/90"
            >
              Next: Review & Update
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
